import { selectStandardProps } from "@/utils/componentUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Col, Form, Input, Modal, Row, Select } from "antd";
import { useTranslation } from "react-i18next";
import { useCreateVehicleBrandMapping } from "../../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { VehicleMappingType } from "../../enums";
import { CreateVehicleMappingFormItem, VehicleMappingData } from "../../types";
import { parseVehicleMappingsFromIdentifiers, VEHICLE_IDENTIFIER_DELIMITER, VEHICLE_TYPES_PATH } from "../../utils";

const colSpan = 6;

interface Props {
  open: boolean;
  selectedMappingTypes: VehicleMappingType[];
  mappingData: VehicleMappingData[];
  onFormCancel: () => void;
}

const VehicleBrandCreateMappingModal = ({ open, selectedMappingTypes, mappingData, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateVehicleMappingFormItem>();

  const createVehicleBrandMapping = useCreateVehicleBrandMapping();

  const handleFormSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        createVehicleBrandMapping.mutate(parseVehicleMappingsFromIdentifiers(values), {
          onError: (error) => setErrorsToForm(form, "vehicle.attrs", error.violations),
          onSuccess: onFormCancel,
        });
      })
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.HUGE}
      open={open}
      confirmLoading={createVehicleBrandMapping.isPending}
      title={t("vehicle.titles.brandCreate")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="vehicleBrandCreateMappingForm">
        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item name="name" label={t("vehicle.attrs.name")} rules={[validations.notBlank]}>
              <Input size="small" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          {Object.values(VehicleMappingType).map((type) => {
            const typePath = VEHICLE_TYPES_PATH[type];
            const mappings = mappingData.find((mapping) => mapping.type === type);

            return selectedMappingTypes.includes(type) ? (
              <Col span={colSpan} key={type}>
                <Form.Item
                  name={typePath.keyIdentifier}
                  label={t(`vehicle.enums.vehicleMappingType.${type}`)}
                  rules={[validations.none]}
                >
                  <Select
                    {...selectStandardProps}
                    allowClear
                    size="small"
                    options={(mappings && mappings.brands ? mappings.brands : []).map((item) => ({
                      value: item.id + VEHICLE_IDENTIFIER_DELIMITER + item.name,
                      label: `${item.name} (${item.id})`,
                    }))}
                  />
                </Form.Item>
              </Col>
            ) : null;
          })}
        </Row>
      </Form>
    </Modal>
  );
};

export default VehicleBrandCreateMappingModal;
